import api from './api';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

const ONBOARDING_STORAGE_KEY = 'himil_onboarding_progress';

const ONBOARDING_STEPS = {
  WELCOME: 'welcome',
  COMPANY: 'company',
  DRIVERS: 'drivers', 
  VEHICLES: 'vehicles',
  DOCUMENTS: 'documents',
  MAINTENANCE: 'maintenance',
  DASHBOARD: 'dashboard'
};

const getCachedFirebaseUid = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const storedUser = window.localStorage.getItem('user');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        return parsed?.uid || parsed?.firebaseUid || parsed?.id || null;
      }

      const cachedUid = window.localStorage.getItem('firebaseUid');
      if (cachedUid) {
        return cachedUid;
      }
    }
  } catch (error) {
    console.warn('Failed to retrieve cached Firebase UID:', error);
  }

  return null;
};

const STEP_VALIDATION_REQUIREMENTS = {
  [ONBOARDING_STEPS.WELCOME]: {
    required: [],
    validation: () => true // Welcome step is informational
  },
  [ONBOARDING_STEPS.COMPANY]: {
    required: ['companyName', 'address', 'contact'],
    validation: (data) => {
      return data?.companyName && 
             data?.address?.street && 
             data?.address?.city && 
             data?.address?.state && 
             data?.contact?.email && 
             data?.contact?.phone;
    },
    onComplete: 'createCompany' // Special action on completion
  },
  [ONBOARDING_STEPS.DRIVERS]: {
    required: [],
    optional: true,
    validation: () => true // Optional step for now
  },
  [ONBOARDING_STEPS.VEHICLES]: {
    required: [],
    optional: true,
    validation: () => true // Optional step for now
  },
  [ONBOARDING_STEPS.DOCUMENTS]: {
    required: [],
    optional: true,
    validation: () => true // Optional step for now
  },
  [ONBOARDING_STEPS.MAINTENANCE]: {
    required: [],
    optional: true,
    validation: () => true // Optional step
  },
  [ONBOARDING_STEPS.DASHBOARD]: {
    required: [],
    validation: () => true // Final step
  }
};

class OnboardingService {
  constructor() {
    this.currentProgress = this.getStoredProgress();
    this.mockData = this.generateMockData();
  }

  // Progress Management
  getStoredProgress() {
    try {
      const stored = localStorage.getItem(ONBOARDING_STORAGE_KEY);
      return stored ? JSON.parse(stored) : this.getDefaultProgress();
    } catch (error) {
      console.error('Error loading onboarding progress:', error);
      return this.getDefaultProgress();
    }
  }

  getDefaultProgress() {
    return {
      isOnboardingComplete: false,
      isOnboardingStarted: false,
      currentStep: ONBOARDING_STEPS.WELCOME,
      completedSteps: [],
      skippedSteps: [],
      stepData: {},
      timestamps: {
        started: null,
        completed: null,
        lastActive: null
      },
      userProfile: null,
      metadata: {
        version: '1.0.0',
        sessionId: this.generateSessionId()
      }
    };
  }

  saveProgress(updates) {
    this.currentProgress = {
      ...this.currentProgress,
      ...updates,
      timestamps: {
        ...this.currentProgress.timestamps,
        lastActive: new Date().toISOString(),
        ...(updates.timestamps || {})
      }
    };

    try {
      localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(this.currentProgress));
      
      // Also save to user profile if authenticated
      this.syncToUserProfile().catch(error => {
        console.warn('Failed to sync onboarding progress to user profile:', error);
      });
      
    } catch (error) {
      console.error('Error saving onboarding progress:', error);
    }

    return this.currentProgress;
  }

  // Step Navigation
  getSteps() {
    return Object.values(ONBOARDING_STEPS);
  }

  getCurrentStep() {
    return this.currentProgress.currentStep;
  }

  getStepIndex(step = this.getCurrentStep()) {
    return this.getSteps().indexOf(step);
  }

  getTotalSteps() {
    return this.getSteps().length;
  }

  getProgress() {
    const completedSteps = this.currentProgress.completedSteps.length;
    const totalSteps = this.getTotalSteps();
    return Math.round((completedSteps / totalSteps) * 100);
  }

  canProgressToStep(targetStep) {
    const currentIndex = this.getStepIndex();
    const targetIndex = this.getStepIndex(targetStep);
    
    // Can go to any previous step
    if (targetIndex <= currentIndex) {
      return true;
    }
    
    // Can only go to next step if current step is completed or skipped
    const currentStep = this.getCurrentStep();
    return this.isStepCompleted(currentStep) || this.isStepSkipped(currentStep);
  }

  async navigateToStep(targetStep, force = false) {
    if (!force && !this.canProgressToStep(targetStep)) {
      throw new Error(`Cannot navigate to step ${targetStep}. Complete current step first.`);
    }

    return this.saveProgress({
      currentStep: targetStep,
      isOnboardingStarted: true,
      timestamps: {
        ...this.currentProgress.timestamps,
        started: this.currentProgress.timestamps.started || new Date().toISOString()
      }
    });
  }

  async nextStep() {
    const currentIndex = this.getStepIndex();
    const steps = this.getSteps();
    
    if (currentIndex >= steps.length - 1) {
      return this.completeOnboarding();
    }
    
    const nextStep = steps[currentIndex + 1];
    return this.navigateToStep(nextStep);
  }

  async previousStep() {
    const currentIndex = this.getStepIndex();
    const steps = this.getSteps();
    
    if (currentIndex <= 0) {
      return this.currentProgress;
    }
    
    const prevStep = steps[currentIndex - 1];
    return this.navigateToStep(prevStep, true);
  }

  // Step Completion
  isStepCompleted(step) {
    return this.currentProgress.completedSteps.includes(step);
  }

  isStepSkipped(step) {
    return this.currentProgress.skippedSteps.includes(step);
  }

  async completeStep(step, data = null, userId = null) {
    console.log('✅✅✅ ONBOARDING: ========== COMPLETE STEP CALLED ==========');
    console.log('✅ ONBOARDING: Step:', step);
    console.log('✅ ONBOARDING: User ID:', userId);
    console.log('✅ ONBOARDING: Has data:', !!data);
    console.log('✅ ONBOARDING: Data received:', JSON.stringify(data, null, 2));
    console.log('✅ ONBOARDING: Data keys:', data ? Object.keys(data) : 'NO DATA');
    
    try {
      if (!userId) {
        console.error('❌ ONBOARDING: No user ID provided for step completion');
        throw new Error('User authentication required');
      }

      console.log('🧪 ONBOARDING: Starting step validation...');
      // Validate step completion if required
      let isValid;
      if (step === 'company') {
        console.log('🏢 ONBOARDING: Using company validation for data:', JSON.stringify(data, null, 2));
        isValid = await this.validateCompanyStepCompletion(data);
      } else {
        console.log('📝 ONBOARDING: Using general validation for step:', step);
        isValid = await this.validateStepCompletion(step, data);
      }
      
      console.log('🧪 ONBOARDING: Validation result:', JSON.stringify(isValid, null, 2));
      
      if (!isValid.valid) {
        console.error('❌ ONBOARDING: Validation failed:', isValid.error);
        throw new Error(`Step validation failed: ${isValid.error}`);
      }

      console.log('💾💾💾 ONBOARDING: ========== SAVING STEP DATA ==========');
      console.log('💾 ONBOARDING: Saving to Firestore - User:', userId, 'Step:', step);
      
      // Save step data to Firestore first
      const stepDataToSave = {
        ...data,
        completedAt: new Date().toISOString(),
        status: 'completed'
      };
      
      console.log('💾 ONBOARDING: Step data being saved:', JSON.stringify(stepDataToSave, null, 2));
      await this.updateCompanyOnboardingData(userId, step, stepDataToSave);
      console.log('✅ ONBOARDING: Step data saved to Firestore successfully');

      // Handle special step completion actions
      const stepConfig = STEP_VALIDATION_REQUIREMENTS[step];
      console.log('🔧 ONBOARDING: Step config:', stepConfig);
      
      // The company document itself is created server-side by the company step's
      // save (/companies/onboarding/company-data) or by /onboarding/complete.

      // Update local progress
      const updates = {
        completedSteps: [...new Set([...this.currentProgress.completedSteps, step])],
        skippedSteps: this.currentProgress.skippedSteps.filter(s => s !== step)
      };

      if (data) {
        updates.stepData = {
          ...this.currentProgress.stepData,
          [step]: {
            ...data,
            completedAt: new Date().toISOString()
          }
        };
      }

      // Mark step as completed in Firestore
      await this.completeCompanyOnboardingStep(userId, step);

      return this.saveProgress(updates);
    } catch (error) {
      console.error('❌ ONBOARDING: Step completion failed:', error);
      throw error;
    }
  }

  async skipStep(step, reason = null) {
    const stepConfig = STEP_VALIDATION_REQUIREMENTS[step];
    
    if (stepConfig && !stepConfig.optional) {
      throw new Error(`Step ${step} is required and cannot be skipped`);
    }

    const updates = {
      skippedSteps: [...new Set([...this.currentProgress.skippedSteps, step])],
      completedSteps: this.currentProgress.completedSteps.filter(s => s !== step)
    };

    if (reason) {
      updates.stepData = {
        ...this.currentProgress.stepData,
        [step]: {
          skipped: true,
          reason,
          skippedAt: new Date().toISOString()
        }
      };
    }

    return this.saveProgress(updates);
  }

  // Step Validation
  async validateStepCompletion(step, data = null) {
    const requirements = STEP_VALIDATION_REQUIREMENTS[step];
    
    if (!requirements) {
      return { valid: true };
    }

    try {
      // For steps that require API validation
      if (requirements.endpoint && !data) {
        const response = await this.fetchStepData(step);
        data = response.data;
      }

      // Run validation function
      if (requirements.validation) {
        const isValid = requirements.validation(data);
        if (!isValid) {
          return { 
            valid: false, 
            error: `Step ${step} validation failed`,
            requirements: requirements.required 
          };
        }
      }

      return { valid: true, data };
    } catch (error) {
      console.error(`Validation error for step ${step}:`, error);
      return { 
        valid: false, 
        error: error.message || 'Validation failed',
        fallbackToMock: true
      };
    }
  }

  async fetchStepData(step) {
    const requirements = STEP_VALIDATION_REQUIREMENTS[step];
    
    if (!requirements?.endpoint) {
      return { data: null };
    }

    try {
      const response = await api.get(requirements.endpoint);
      return { 
        data: response.data, 
        source: 'api',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.warn(`API fetch failed for step ${step}, using mock data:`, error);
      return { 
        data: this.mockData[step] || null, 
        source: 'mock',
        error: error.message,
        timestamp: new Date().toISOString()
      };
    }
  }

  // Onboarding Completion
  async completeOnboarding() {
    const progress = this.saveProgress({
      isOnboardingComplete: true,
      currentStep: ONBOARDING_STEPS.DASHBOARD,
      timestamps: {
        ...this.currentProgress.timestamps,
        completed: new Date().toISOString()
      }
    });

    // Sync final state to user profile
    try {
      await this.syncToUserProfile();
    } catch (error) {
      console.warn('Failed to sync final onboarding state:', error);
    }

    return progress;
  }

  async restartOnboarding() {
    return this.saveProgress(this.getDefaultProgress());
  }

  // User Profile Sync
  async syncToUserProfile() {
    try {
      const firebaseUid = getCachedFirebaseUid();
      if (!firebaseUid) {
        console.warn('Skipping onboarding sync: no Firebase UID available');
        return;
      }

      // Save onboarding progress to user profile
      await api.patch('/users/profile', {
        onboardingProgress: {
          ...this.currentProgress,
          syncedAt: new Date().toISOString()
        }
      }, {
        headers: { uid: firebaseUid },
        params: { firebaseUid }
      });
    } catch (error) {
      // Non-critical error - onboarding can continue without sync
      console.warn('Failed to sync onboarding progress to profile:', error);
    }
  }

  async loadFromUserProfile() {
    try {
      const firebaseUid = getCachedFirebaseUid();
      if (!firebaseUid) {
        console.warn('Skipping onboarding progress load: no Firebase UID available');
        return this.currentProgress;
      }

      const response = await api.get('/users/profile', {
        headers: { uid: firebaseUid },
        params: { firebaseUid }
      });
      const serverProgress = response.data?.user?.onboardingProgress;
      
      if (serverProgress && serverProgress.syncedAt) {
        // Merge server progress with local progress, preferring newer data
        const localTimestamp = new Date(this.currentProgress.timestamps?.lastActive || 0);
        const serverTimestamp = new Date(serverProgress.syncedAt);
        
        if (serverTimestamp > localTimestamp) {
          this.currentProgress = { ...serverProgress };
          localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(this.currentProgress));
        }
      }
    } catch (error) {
      console.warn('Failed to load onboarding progress from profile:', error);
    }
    
    return this.currentProgress;
  }

  // Mock Data Generation
  generateMockData() {
    return {
      [ONBOARDING_STEPS.WELCOME]: {
        company: {
          id: 'mock-company-id',
          name: 'Demo Himil Fleet Company',
          dotNumber: '123456',
          mcNumber: 'MC123456',
          fleetSize: 0,
          isActive: true
        }
      },
      [ONBOARDING_STEPS.DRIVERS]: [
        {
          id: 'mock-driver-1',
          firstName: 'John',
          lastName: 'Driver',
          cdlNumber: 'D123456789',
          cdlClass: 'Class A',
          status: 'active'
        }
      ],
      [ONBOARDING_STEPS.VEHICLES]: [
        {
          id: 'mock-vehicle-1',
          vehicleNumber: 'TRK001',
          make: 'Freightliner',
          model: 'Cascadia',
          year: 2023,
          status: 'active'
        }
      ],
      [ONBOARDING_STEPS.DOCUMENTS]: {
        documents: [
          {
            id: 'mock-doc-1',
            title: 'CDL License',
            documentType: 'CDL_LICENSE',
            category: 'driver',
            status: 'active'
          }
        ]
      }
    };
  }

  generateSessionId() {
    return `onboarding_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  // Utility Methods
  getStepDisplayName(step) {
    const displayNames = {
      [ONBOARDING_STEPS.WELCOME]: 'Welcome & Company Setup',
      [ONBOARDING_STEPS.DRIVERS]: 'Add Your First Driver',
      [ONBOARDING_STEPS.VEHICLES]: 'Add Your First Vehicle', 
      [ONBOARDING_STEPS.DOCUMENTS]: 'Upload Important Documents',
      [ONBOARDING_STEPS.MAINTENANCE]: 'Schedule Maintenance (Optional)',
      [ONBOARDING_STEPS.DASHBOARD]: 'Explore Your Dashboard'
    };
    return displayNames[step] || step;
  }

  getStepDescription(step) {
    const descriptions = {
      [ONBOARDING_STEPS.WELCOME]: 'Complete your company profile to get started with compliance management',
      [ONBOARDING_STEPS.DRIVERS]: 'Add your first driver with CDL information for compliance tracking',
      [ONBOARDING_STEPS.VEHICLES]: 'Register your first vehicle with registration and insurance details',
      [ONBOARDING_STEPS.DOCUMENTS]: 'Upload essential documents like licenses and certificates',
      [ONBOARDING_STEPS.MAINTENANCE]: 'Schedule preventive maintenance to keep your fleet running smoothly',
      [ONBOARDING_STEPS.DASHBOARD]: 'Take a tour of your dashboard and learn about key features'
    };
    return descriptions[step] || '';
  }

  getStepIcon(step) {
    const icons = {
      [ONBOARDING_STEPS.WELCOME]: '🏢',
      [ONBOARDING_STEPS.DRIVERS]: '👨‍💼',
      [ONBOARDING_STEPS.VEHICLES]: '🚛',
      [ONBOARDING_STEPS.DOCUMENTS]: '📄',
      [ONBOARDING_STEPS.MAINTENANCE]: '🔧',
      [ONBOARDING_STEPS.DASHBOARD]: '📊'
    };
    return icons[step] || '📋';
  }

  // Analytics & Insights
  getOnboardingAnalytics() {
    const started = this.currentProgress.timestamps.started;
    const completed = this.currentProgress.timestamps.completed;
    const duration = started && completed ? 
      new Date(completed) - new Date(started) : null;

    return {
      isComplete: this.currentProgress.isOnboardingComplete,
      progress: this.getProgress(),
      currentStep: this.getCurrentStep(),
      completedSteps: this.currentProgress.completedSteps.length,
      skippedSteps: this.currentProgress.skippedSteps.length,
      totalSteps: this.getTotalSteps(),
      duration: duration ? Math.round(duration / 1000 / 60) : null, // minutes
      sessionId: this.currentProgress.metadata?.sessionId,
      timestamps: this.currentProgress.timestamps
    };
  }

  // Company Onboarding Wizard Methods (server-side via /api/onboarding; Firestore
  // rules deny the browser direct access to the progress document)
  async getCompanyOnboardingProgress(userId) {
    try {
      const response = await api.get('/onboarding/progress');
      return response?.progress || null;
    } catch (error) {
      console.error('Error getting company onboarding progress:', error);
      throw error;
    }
  }

  async getCompanyDraft(userId) {
    try {
      const response = await api.get('/onboarding/company-draft');
      if (!response?.company) {
        console.warn('⚠️ ONBOARDING: No company draft available');
        return null;
      }
      return response.company;
    } catch (error) {
      console.error('Error getting company draft document:', error);
      throw error;
    }
  }

  async updateCompanyOnboardingData(userId, stepKey, stepData) {
    try {
      console.log('📝 ONBOARDING: Saving step data:', { stepKey, keys: stepData ? Object.keys(stepData) : [] });
      const result = await api.patch(`/onboarding/progress/data/${encodeURIComponent(stepKey)}`, { data: stepData || {} });
      console.log('✅ ONBOARDING: Company onboarding data updated');
      return result;
    } catch (error) {
      console.error('❌ ONBOARDING: Error updating company onboarding step data:', error);
      throw error;
    }
  }

  async completeCompanyOnboardingStep(userId, stepId) {
    try {
      console.log('✅ ONBOARDING: Completing company onboarding step:', { stepId });
      return await api.post('/onboarding/progress/completed-steps', { step: stepId });
    } catch (error) {
      console.error('❌ ONBOARDING: Error completing company onboarding step:', error);
      throw error;
    }
  }

  async updateCompanyOnboardingCurrentStep(userId, stepId) {
    try {
      console.log('💾 ONBOARDING: Updating company onboarding current step:', { stepId });
      return await api.patch('/onboarding/progress/current-step', { step: stepId });
    } catch (error) {
      console.error('❌ ONBOARDING: Error updating company onboarding current step:', error);
      throw error;
    }
  }

  // Server-side: creates the company if the company step never finished,
  // checks the user owns it, and marks progress, company and user as onboarded.
  async finalizeOnboarding(userId, {
    welcomeData = {},
    totalSteps = 4,
    completedSteps
  } = {}) {
    const response = await api.post('/onboarding/complete', {
      welcomeData,
      totalSteps,
      completedSteps: Array.isArray(completedSteps) ? completedSteps : []
    });
    if (!response?.companyId) {
      throw new Error('Onboarding completed without a company ID');
    }
    return { companyId: response.companyId };
  }

  async initializeCompanyOnboardingProgress(userId) {
    try {
      const response = await api.post('/onboarding/progress');
      return response?.progress || null;
    } catch (error) {
      console.error('Error initializing company onboarding progress:', error);
      throw error;
    }
  }

  async getCompanyOnboardingStatus(userId) {
    try {
      const progress = await this.getCompanyOnboardingProgress(userId);
      
      if (!progress) {
        return {
          hasStarted: false,
          isCompleted: false,
          currentStep: 1,
          completedSteps: [],
          lastUpdated: null,
          data: {
            company: {},
            adminUser: {},
            configuration: {},
            welcome: {}
          }
        };
      }

      return {
        hasStarted: true,
        isCompleted: progress.isCompleted || false,
        currentStep: progress.currentStep || 1,
        completedSteps: progress.completedSteps || [],
        lastUpdated: progress.lastUpdated,
        companyId: progress.companyId || null,
        data: progress.data || {
          company: {},
          adminUser: {},
          configuration: {},
          welcome: {}
        }
      };
    } catch (error) {
      console.error('Error getting company onboarding status:', error);
      throw error;
    }
  }

  async getUserProfile(userId) {
    try {
      if (!userId) {
        throw new Error('User ID is required to fetch profile');
      }

      const userRef = doc(db, 'users', userId);
      const snapshot = await getDoc(userRef);

      if (!snapshot.exists()) {
        console.warn('⚠️ ONBOARDING: No user profile found in Firestore', { userId });
        return null;
      }

      const data = snapshot.data();
      console.log('✅ ONBOARDING: User profile fetched from Firestore', {
        userId,
        populatedFields: Object.keys(data || {})
      });
      return data;
    } catch (error) {
      console.error('❌ ONBOARDING: Failed to fetch user profile from Firestore:', error);
      throw error;
    }
  }

  // Incremental Saving Methods
  async saveCompanyDataIncremental(userId, companyData, isPartial = true) {
    try {
      const stepData = {
        ...companyData,
        isPartial,
        lastSaved: new Date().toISOString(),
        ...(isPartial ? {} : { readyForSubmission: true })
      };
      return await this.updateCompanyOnboardingData(userId, 'company', stepData);
    } catch (error) {
      console.error('❌ ONBOARDING: Error saving company data incrementally:', error);
      throw error;
    }
  }

  async saveCompanyDataViaApi(userId, companyData, isPartial = true) {
    try {
      console.log('📡 ONBOARDING: Sending company data to backend API', {
        userId,
        isPartial,
        keys: Object.keys(companyData || {})
      });

      const payload = {
        firebaseUid: userId,
        isPartial,
        company: companyData
      };

      const response = await api.post('/companies/onboarding/company-data', payload);
      const result = response?.data?.result || response?.data || {};

      if (result?.companyId) {
        this.currentProgress = {
          ...(this.currentProgress || {}),
          companyId: result.companyId,
          data: {
            ...(this.currentProgress?.data || {}),
            company: {
              ...(this.currentProgress?.data?.company || {}),
              ...companyData,
              companyId: result.companyId
            }
          }
        };
      }

      console.log('✅ ONBOARDING: Backend acknowledged company data save', {
        companyId: result?.companyId,
        isPartial: result?.isPartial,
        lastUpdated: result?.lastUpdated
      });

      return result;
    } catch (error) {
      console.error('❌ ONBOARDING: Backend save for company data failed:', error);
      throw error;
    }
  }

  async saveConfigurationDataViaApi(userId, configurationData, isPartial = true) {
    try {
      console.log('📡 ONBOARDING: Sending configuration data to backend API', {
        userId,
        isPartial,
        keys: Object.keys(configurationData || {})
      });

      const payload = {
        firebaseUid: userId,
        isPartial,
        configuration: configurationData
      };

      const response = await api.post('/companies/onboarding/configuration', payload);
      const result = response?.data?.result || response?.data || {};

      if (result?.companyId) {
        this.currentProgress = {
          ...(this.currentProgress || {}),
          companyId: result.companyId,
          data: {
            ...(this.currentProgress?.data || {}),
            configuration: {
              ...(this.currentProgress?.data?.configuration || {}),
              ...configurationData
            }
          }
        };
      }

      console.log('✅ ONBOARDING: Backend acknowledged configuration data save', {
        companyId: result?.companyId,
        isPartial: result?.isPartial,
        lastUpdated: result?.lastUpdated
      });

      return result;
    } catch (error) {
      console.error('❌ ONBOARDING: Backend save for configuration data failed:', error);
      throw error;
    }
  }

  async saveUserDataIncremental(userId, userData, isPartial = true) {
    try {
      console.log('💾 ONBOARDING: Saving user data incrementally:', { isPartial });
      const stepData = {
        ...userData,
        isPartial,
        lastSaved: new Date().toISOString(),
        ...(isPartial ? {} : { readyForSubmission: true })
      };
      const result = await this.updateCompanyOnboardingData(userId, 'adminUser', stepData);
      console.log('✅ ONBOARDING: User data saved incrementally');
      return result;
    } catch (error) {
      console.error('❌ ONBOARDING: Error saving user data incrementally:', error);
      throw error;
    }
  }

  async getAllStepData(userId = null) {
    try {
      if (!userId) {
        console.warn('No user ID provided for getting step data');
        return {};
      }

      const progress = await this.getCompanyOnboardingProgress(userId);
      return progress?.data || {};
    } catch (error) {
      console.error('Error getting all step data:', error);
      return {};
    }
  }

  async updateProgress(updates, userId = null) {
    // Wizard progress lives server-side now; this local copy is all that is left.
    return this.saveProgress(updates);
  }

  // Enhanced validation for company step completion
  async validateCompanyStepCompletion(companyData) {
    console.log('🔍 ONBOARDING: validateCompanyStepCompletion called with:', companyData ? Object.keys(companyData) : 'null');
    try {
      const requiredFields = {
        'companyName': companyData?.companyName || companyData?.name,
        'address.street': companyData?.address?.street,
        'address.city': companyData?.address?.city,
        'address.state': companyData?.address?.state,
        'address.zipCode': companyData?.address?.zipCode,
        'contact.phone': companyData?.contact?.phone,
        'contact.email': companyData?.contact?.email
      };
      
      console.log('🔍 ONBOARDING: Required fields check:', requiredFields);

      const missingFields = [];
      Object.entries(requiredFields).forEach(([field, value]) => {
        if (!value || (typeof value === 'string' && value.trim().length === 0)) {
          missingFields.push(field);
        }
      });

      if (missingFields.length > 0) {
        console.log('❌ ONBOARDING: Validation failed - missing fields:', missingFields);
        return {
          valid: false,
          error: `Missing required fields: ${missingFields.join(', ')}`,
          missingFields
        };
      }
      
      console.log('✅ ONBOARDING: All required fields present');

      // Validate formats
      if (companyData.contact?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyData.contact.email)) {
        return { valid: false, error: 'Invalid email format' };
      }

      if (companyData.dotNumber && !/^\d{1,8}$/.test(companyData.dotNumber)) {
        return { valid: false, error: 'Invalid DOT number format (1-8 digits)' };
      }

      return { valid: true, data: companyData };
    } catch (error) {
      return { valid: false, error: error.message };
    }
  }
}

// Export singleton instance
const onboardingService = new OnboardingService();

export default onboardingService;
export { onboardingService, ONBOARDING_STEPS };
